import React from 'react'

import { Trans } from '@disco/i18n/trans'
import { useTranslation } from '@disco/i18n/use-translation'

import { Text } from 'components/ds'

// The search API stops counting matches here.
const SEARCH_TOTAL_CAP = 10000

type Props = {
  loaded: number
  /** Omitted when the source's total can't be trusted (AIMS). */
  total?: number
  hasMore: boolean
}

/**
 * "Showing 50 of 2,611 tracks" under the search results table. Without a
 * total it shows the loaded count only, with a "+" while more pages remain.
 */
const ResultsCount = ({ loaded, total, hasMore }: Props) => {
  const { t, i18n } = useTranslation('search')
  const format = (n: number) => n.toLocaleString(i18n.language)
  const loadedCount = format(loaded)
  // Named tags, not the children form's indexes, so a translation can reorder
  // the numbers without depending on the JSX nesting.
  const components = {
    loadedCount: <Text weight="bold" highContrast />,
    totalCount: <Text weight="bold" highContrast />,
  }

  if (total === undefined) {
    return hasMore ? (
      <Trans
        t={t}
        i18nKey="search:loadedTracksMore"
        defaults="Showing <loadedCount>{{loadedCount}}+</loadedCount> tracks"
        values={{ loadedCount }}
        components={components}
      />
    ) : (
      <Trans
        t={t}
        i18nKey="search:loadedTracks"
        defaults="Showing <loadedCount>{{loadedCount}}</loadedCount> tracks"
        count={loaded}
        values={{ loadedCount }}
        components={components}
      />
    )
  }

  // Its own key so a locale can word "more than" its own way, not with "+".
  if (total >= SEARCH_TOTAL_CAP) {
    return (
      <Trans
        t={t}
        i18nKey="search:resultsCountCapped"
        defaults="Showing <loadedCount>{{loadedCount}}</loadedCount> of <totalCount>{{totalCount}}+</totalCount> tracks"
        values={{ loadedCount, totalCount: format(SEARCH_TOTAL_CAP) }}
        components={components}
      />
    )
  }

  return (
    <Trans
      t={t}
      i18nKey="search:resultsCount"
      defaults="Showing <loadedCount>{{loadedCount}}</loadedCount> of <totalCount>{{totalCount}}</totalCount> tracks"
      count={total}
      values={{ loadedCount, totalCount: format(total) }}
      components={components}
    />
  )
}

export default ResultsCount
